/**
 * TajwidColoredText — render Arabic text with rule-based highlighting.
 *
 * Wraps each annotated slice in a <span> with a tone color and optional
 * tooltip showing the rule label. If `annotations` is empty, just returns
 * the plain text.
 */
import React from 'react';

/**
 * @param {string} text
 * @param {Array<{rule:string, label:string, color:string, start:number, end:number, harakat:number}>} annotations
 * @param {string} [className]
 */
export const TajwidColoredText = ({ text, annotations = [], className = '' }) => {
  if (!text) return null;
  if (!annotations.length) {
    return <span className={className}>{text}</span>;
  }
  // Sort and merge overlaps (later annotation wins)
  const sorted = [...annotations].sort((a, b) => a.start - b.start || a.end - b.end);
  const segs = [];
  let cursor = 0;
  for (const a of sorted) {
    if (a.start < cursor) continue; // skip overlap
    if (a.start > cursor) {
      segs.push({ text: text.slice(cursor, a.start), rule: null });
    }
    segs.push({
      text: text.slice(a.start, a.end),
      rule: a.rule,
      label: a.label,
      color: a.color,
      harakat: a.harakat,
    });
    cursor = a.end;
  }
  if (cursor < text.length) {
    segs.push({ text: text.slice(cursor), rule: null });
  }

  return (
    <span className={className}>
      {segs.map((s, i) =>
        s.rule ? (
          <span
            key={i}
            data-tajwid-rule={s.rule}
            data-testid={`tajwid-${s.rule}`}
            title={`${s.label} — ${s.harakat} ḥarakāt`}
            style={{ color: s.color, fontWeight: 600 }}
          >
            {s.text}
          </span>
        ) : (
          <span key={i}>{s.text}</span>
        )
      )}
    </span>
  );
};

export default TajwidColoredText;
